// KallelseModal.js - Modal för att välja mall och skicka kallelse till patient
// Kanaler: e-post (default), SMS, brev (Ekopost).
import React, { useState, useEffect } from 'react';
import apiClient from '../../apiClient';
import { toast } from 'react-toastify';
import {
  fetchPatientFolkbokforing, ensureRealAppointment, isVirtualOccurrence, occurrenceDateOf
} from './calendarApi';
import './KallelseModal.css';

const CHANNELS = [
  { key: 'email', label: 'E-post' },
  { key: 'sms', label: 'SMS' },
  { key: 'letter', label: 'Brev' },
];

const KallelseModal = ({ appointment, preSelectedTemplate, onClose }) => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [activeTab, setActiveTab] = useState('send'); // 'send' eller 'history'
  const [channel, setChannel] = useState('email');
  const [ekopostEnabled, setEkopostEnabled] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  // SPAR-uppslag (folkbokföring) — bara klinik med aktivering
  const [sparAvailable, setSparAvailable] = useState(false);
  // Skydd/avregistrering kan ändras av ett SPAR-uppslag i modalen
  const [sparFlags, setSparFlags] = useState({ protected: false, deregistered: false, reason: null });
  // Lokal kopia av postadress (kan uppdateras av folkbokföringsuppslag)
  const [postal, setPostal] = useState({ street: '', postal_code: '', city: '' });

  // Hämta mallar, historik och inställningar
  useEffect(() => {
    if (!appointment) return;

    apiClient.get('/api/kallelsemallar')
      .then(res => {
        setTemplates(res.data);
        if (preSelectedTemplate) {
          setSelectedTemplateId(preSelectedTemplate.template_id);
        } else if (res.data.length > 0) {
          setSelectedTemplateId(res.data[0].template_id);
        }
      })
      .catch(err => console.error('Fel vid hämtning av mallar:', err));

    // Ett virtuellt tillfälle i en serie (Spår R) har ingen egen historik än -
    // seriens id skulle visa första tillfällets kallelser
    if (isVirtualOccurrence(appointment)) {
      setHistory([]);
    } else {
      apiClient.get(`/api/kallelsemallar/history/${appointment.appointment_id}`)
        .then(res => setHistory(res.data))
        .catch(err => console.error('Fel vid hämtning av historik:', err));
    }

    // Är brevutskick (Ekopost) aktiverat för kliniken?
    apiClient.get('/api/calendar/settings')
      .then(res => setEkopostEnabled(!!res.data.ekopost_enabled))
      .catch(() => setEkopostEnabled(false));

    apiClient.get('/api/journal/clinic/settings')
      .then(res => setSparAvailable(!!res.data.spar_lookup_available))
      .catch(() => setSparAvailable(false));
    setSparFlags({
      protected: !!appointment.patient?.protected_identity,
      deregistered: !!appointment.patient?.deceased_or_deregistered,
      reason: appointment.patient?.deregistration_reason || null
    });

    // Initiera postadress från patientkortet
    setPostal({
      street: appointment.patient?.street || '',
      postal_code: appointment.patient?.postal_code || '',
      city: appointment.patient?.city || ''
    });
  }, [appointment]);

  // Hämta förhandsgranskning när mall väljs
  useEffect(() => {
    if (!selectedTemplateId || !appointment) return;

    setLoading(true);
    apiClient.post('/api/kallelsemallar/preview', {
      template_id: selectedTemplateId,
      // Virtuellt tillfälle (Spår R): förhandsgranska med tillfällets datum
      // utan att skapa bokningen - den skapas först när kallelsen skickas
      ...(isVirtualOccurrence(appointment)
        ? {
            appointment_id: appointment.original_appointment_id || appointment.appointment_id,
            occurrence_date: occurrenceDateOf(appointment)
          }
        : { appointment_id: appointment.appointment_id })
    })
      .then(res => {
        setPreview(res.data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Fel vid förhandsgranskning:', err);
        setLoading(false);
      });
  }, [selectedTemplateId, appointment]);

  const patientEmail = appointment?.patient?.email;
  const patientPhone = appointment?.patient?.phone;
  const patientId = appointment?.patient?.patient_id;
  const patientName = appointment?.patient?.fullname ||
    `${appointment?.patient?.firstname || ''} ${appointment?.patient?.lastname || ''}`.trim();
  const hasPostalAddress = !!(postal.street && postal.postal_code && postal.city);

  // Kan vi skicka på vald kanal?
  // Folkbokföringsspärrar (SPAR): avregistrerad → inga kallelser alls;
  // skyddade personuppgifter → ingen brevkanal (backend spärrar också)
  const canSend = (
    (channel === 'email' && !!patientEmail) ||
    (channel === 'sms' && !!patientPhone) ||
    (channel === 'letter' && ekopostEnabled && hasPostalAddress && !sparFlags.protected)
  ) && !sparFlags.deregistered && !!selectedTemplateId && templates.length > 0;

  const handleFetchAddress = async () => {
    if (!patientId) return;
    try {
      setLookingUp(true);
      // Utan force: sparad adress återanvänds om den kontrollerats senaste 30 dagarna
      const res = await fetchPatientFolkbokforing(patientId);
      const p = res.patient || {};
      setPostal({
        street: p.street || '',
        postal_code: p.postal_code || '',
        city: p.city || ''
      });
      setSparFlags({
        protected: !!p.protected_identity,
        deregistered: !!p.deceased_or_deregistered,
        reason: p.deregistration_reason || null
      });
      if (p.protected_identity) {
        toast.warning('Skyddade personuppgifter — brev kan inte skickas');
      } else {
        toast.success(res.from_cache ? 'Adressen är kontrollerad mot SPAR senaste 30 dagarna' : 'Adress hämtad från SPAR');
      }
    } catch (err) {
      toast.info(err.response?.data?.error || 'Adressuppslag ej tillgängligt - fyll i adress i patientkortet');
    } finally {
      setLookingUp(false);
    }
  };

  const handleSend = async () => {
    if (!canSend) return;

    setSending(true);
    try {
      // Kallelsen gäller tillfällets egen bokning (Spår R) - skapas här, vid skick
      const real = await ensureRealAppointment(appointment);
      const res = await apiClient.post('/api/kallelsemallar/send', {
        template_id: selectedTemplateId,
        appointment_id: real.appointment_id,
        channel
      });
      const dest = res.data.recipient_email || res.data.recipient_address || 'mottagaren';
      toast.success(`Kallelse skickad (${channelLabel(channel)}) till ${dest}`);

      apiClient.get(`/api/kallelsemallar/history/${real.appointment_id}`)
        .then(r => setHistory(r.data));

      setTimeout(() => onClose(), 1500);
    } catch (err) {
      console.error('Fel vid skickande:', err);
      toast.error(err.response?.data?.msg || 'Fel vid skickande av kallelse');
    } finally {
      setSending(false);
    }
  };

  if (!appointment) return null;

  return (
    <div className="kallelse-modal-overlay" onClick={onClose}>
      <div className="kallelse-modal" onClick={e => e.stopPropagation()}>
        <div className="kallelse-modal-header">
          <h3>Skicka kallelse</h3>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>

        <div className="kallelse-modal-tabs">
          <button
            className={`tab-button ${activeTab === 'send' ? 'active' : ''}`}
            onClick={() => setActiveTab('send')}
          >
            Skicka kallelse
          </button>
          <button
            className={`tab-button ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            Historik ({history.length})
          </button>
        </div>

        <div className="kallelse-modal-body">
          {activeTab === 'send' ? (
            <>
              <div className="patient-info">
                <p><strong>Patient:</strong> {patientName}</p>
              </div>

              {sparFlags.deregistered && (
                <div className="no-email-warning">
                  <i className="fas fa-exclamation-triangle"></i>
                  <p>
                    Patienten är avregistrerad i folkbokföringen
                    {sparFlags.reason ? ` (${sparFlags.reason})` : ''} — kallelser skickas inte.
                  </p>
                </div>
              )}

              {/* Kanalval */}
              <div className="form-group">
                <label>Skicka via</label>
                <div className="channel-selector" style={{ display: 'flex', gap: '8px' }}>
                  {CHANNELS.map(c => {
                    const disabled = c.key === 'letter' && (!ekopostEnabled || sparFlags.protected);
                    return (
                      <button
                        key={c.key}
                        type="button"
                        className={`tab-button ${channel === c.key ? 'active' : ''}`}
                        onClick={() => setChannel(c.key)}
                        disabled={disabled}
                        title={!disabled ? '' : (sparFlags.protected
                          ? 'Skyddade personuppgifter — brev skickas inte'
                          : 'Brevutskick är inte aktiverat för kliniken (kalenderinställningar)')}
                      >
                        {c.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Kanalspecifik mottagarinfo/varningar */}
              {channel === 'email' && (
                <div className="patient-info">
                  <p>
                    <strong>E-post:</strong> {patientEmail ||
                      <span className="no-email">Ingen e-postadress registrerad</span>}
                  </p>
                  {!patientEmail && (
                    <div className="no-email-warning">
                      <i className="fas fa-exclamation-triangle"></i>
                      <p>Patienten saknar e-postadress. Lägg till en i patientkortet.</p>
                    </div>
                  )}
                </div>
              )}

              {channel === 'sms' && (
                <div className="patient-info">
                  <p>
                    <strong>Telefon:</strong> {patientPhone ||
                      <span className="no-email">Inget telefonnummer registrerat</span>}
                  </p>
                  {!patientPhone && (
                    <div className="no-email-warning">
                      <i className="fas fa-exclamation-triangle"></i>
                      <p>Patienten saknar telefonnummer. Lägg till ett i patientkortet.</p>
                    </div>
                  )}
                </div>
              )}

              {channel === 'letter' && (
                <div className="patient-info">
                  <p><strong>Postadress:</strong></p>
                  {hasPostalAddress ? (
                    <p style={{ whiteSpace: 'pre-line' }}>
                      {patientName}{'\n'}{postal.street}{'\n'}{postal.postal_code} {postal.city}
                    </p>
                  ) : (
                    <div className="no-email-warning">
                      <i className="fas fa-exclamation-triangle"></i>
                      <p>Patienten saknar fullständig postadress (gata, postnummer, ort).</p>
                    </div>
                  )}
                  {sparAvailable && (
                    <button
                      type="button"
                      className="btn btn-link"
                      style={{ padding: 0 }}
                      onClick={handleFetchAddress}
                      disabled={lookingUp || !patientId}
                    >
                      {lookingUp ? 'Hämtar...' : 'Hämta adress från SPAR'}
                    </button>
                  )}
                  {!ekopostEnabled && (
                    <p className="no-email">Brevutskick är inte aktiverat för kliniken (aktivera i kalenderinställningar).</p>
                  )}
                </div>
              )}

              {/* Mallval + förhandsgranskning */}
              <div className="form-group">
                <label htmlFor="template">Välj mall</label>
                <select
                  id="template"
                  value={selectedTemplateId}
                  onChange={(e) => setSelectedTemplateId(e.target.value)}
                  disabled={templates.length === 0}
                >
                  {templates.length === 0 ? (
                    <option value="">Inga mallar tillgängliga</option>
                  ) : (
                    templates.map(t => (
                      <option key={t.template_id} value={t.template_id}>
                        {t.template_name}
                      </option>
                    ))
                  )}
                </select>
              </div>

              {loading ? (
                <div className="preview-loading">Laddar förhandsgranskning...</div>
              ) : preview && (
                <div className="preview-section">
                  <h4>Förhandsgranskning</h4>
                  {channel === 'email' && (
                    <div className="preview-subject">
                      <strong>Ämne:</strong> {preview.subject}
                    </div>
                  )}
                  <div className="preview-body">
                    {preview.body}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="history-section">
              {history.length === 0 ? (
                <p className="no-history">Inga kallelser har skickats för denna bokning.</p>
              ) : (
                <div className="history-list">
                  {history.map(h => (
                    <div key={h.sent_id} className={`history-item ${h.status}`}>
                      <div className="history-header">
                        <span className="history-date">
                          {new Date(h.sent_at).toLocaleString('sv-SE')}
                        </span>
                        <span className={`history-status status-${h.status}`}>
                          {h.status === 'sent' ? 'Skickad' : 'Misslyckades'}
                        </span>
                      </div>
                      <div className="history-details">
                        <p><strong>Kanal:</strong> {channelLabel(h.channel)}</p>
                        <p><strong>Mall:</strong> {h.template_name || 'Okänd mall'}</p>
                        <p><strong>Till:</strong> {h.recipient_email || h.recipient_address || '-'}</p>
                        {h.channel === 'email' && <p><strong>Ämne:</strong> {h.subject}</p>}
                        <p><strong>Skickad av:</strong> {h.sent_by_name}</p>
                        {h.error_message && (
                          <p className="error-message"><strong>Fel:</strong> {h.error_message}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="kallelse-modal-footer">
          <button className="cancel-button" onClick={onClose}>
            Stäng
          </button>
          {activeTab === 'send' && (
            <button
              className="send-button"
              onClick={handleSend}
              disabled={sending || !canSend}
            >
              {sending ? 'Skickar...' : `Skicka ${channelLabel(channel).toLowerCase()}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

function channelLabel(key) {
  const c = CHANNELS.find(x => x.key === key);
  return c ? c.label : (key || 'E-post');
}

export default KallelseModal;
